import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Check, Bell, Lightbulb, ListTodo } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Textarea, Field, PageHeader, Panel } from "@/components/common";
import RecordPicker from "@/components/RecordPicker";
import Modal from "@/components/Modal";

const KINDS = ["task", "note", "call", "meeting", "introduction", "follow_up"];
const PRIORITIES = ["low", "medium", "high"];

function bucketOf(a) {
  if (a.status === "done") return "completed";
  if (!a.due_date) return "upcoming";
  const d = new Date(a.due_date);
  const now = new Date();
  if (d < now) return "overdue";
  const soon = new Date(now.getTime() + 7 * 864e5);
  return d <= soon ? "due" : "upcoming";
}

export default function Actions() {
  const { workspaceId, canEdit } = useAuth();
  const [acts, setActs] = useState(null);
  const [entities, setEntities] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [followFrom, setFollowFrom] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const [a, e] = await Promise.all([
      api.get(`/activities?workspace_id=${workspaceId}`),
      api.get(`/entities?workspace_id=${workspaceId}&limit=500`),
    ]);
    setActs(a.data);
    setEntities(e.data.items);
  }, [workspaceId]);

  useEffect(() => { setActs(null); load(); }, [load]);

  const complete = async (id) => { await api.post(`/activities/${id}/complete?workspace_id=${workspaceId}`); toast.success("Completed"); load(); };

  const buckets = { overdue: [], due: [], upcoming: [], completed: [] };
  (acts || []).forEach((a) => buckets[bucketOf(a)].push(a));

  const suggestions = buildSuggestions(acts || [], entities);
  const nameOf = (id) => entities.find((e) => e.id === id)?.name || "";

  const SECTIONS = [
    { k: "overdue", l: "Overdue", color: "var(--si-danger)" },
    { k: "due", l: "Due (next 7 days)", color: "var(--si-warning)" },
    { k: "upcoming", l: "Upcoming", color: "var(--si-silver)" },
    { k: "completed", l: "Completed", color: "var(--si-success)" },
  ];

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Actions" sub="Tasks, introductions, follow-ups, reminders, and outcomes."
        actions={canEdit && <Btn variant="primary" data-testid="new-task-btn" onClick={() => setCreateOpen(true)}><Plus size={15} /> New action</Btn>} />

      {suggestions.length > 0 && (
        <Panel className="mb-6">
          <div className="flex items-center gap-2 px-5 py-3 si-label" style={{ borderBottom: "1px solid var(--si-border)" }}><Lightbulb size={13} /> Suggested actions</div>
          {suggestions.map((s, i) => (
            <div key={i} className="px-5 py-3 text-sm" style={{ borderBottom: "1px solid var(--si-border)" }}>
              <span style={{ color: "var(--si-text)" }}>{s.text}</span>
              <div className="si-label mt-0.5">Why: {s.reason}</div>
            </div>
          ))}
        </Panel>
      )}

      {!acts ? <Loading /> : acts.length === 0 ? (
        <EmptyState icon={ListTodo} title="No actions yet" hint="Create a task, follow-up, or introduction." />
      ) : (
        <div className="space-y-6">
          {SECTIONS.map((sec) => buckets[sec.k].length > 0 && (
            <div key={sec.k}>
              <div className="si-label mb-2" style={{ color: sec.color }}>{sec.l} ({buckets[sec.k].length})</div>
              <Panel>
                {buckets[sec.k].map((a) => (
                  <div key={a.id} data-testid={`task-${a.id}`} className="flex items-start justify-between px-5 py-3.5 gap-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="si-label">{a.kind.replace(/_/g, " ")}</span>
                        <Badge value={a.priority === "high" ? "disputed" : a.priority === "medium" ? "uncertain" : "candidate"}>{a.priority}</Badge>
                        {a.reminder_date && <span className="si-label flex items-center gap-1"><Bell size={10} /> {new Date(a.reminder_date).toLocaleDateString()}</span>}
                      </div>
                      <div className="text-sm mt-1" style={{ color: "var(--si-text)" }}>{a.title}</div>
                      {a.description && <div className="text-xs mt-0.5" style={{ color: "var(--si-text-2)" }}>{a.description}</div>}
                      {(a.entity_ids || []).length > 0 && <div className="si-label mt-1">{a.entity_ids.map(nameOf).filter(Boolean).join(", ")}</div>}
                      {a.outcome && <div className="text-xs mt-1" style={{ color: "var(--si-success)" }}>Outcome: {a.outcome}</div>}
                      {a.due_date && <div className="si-label mt-1">due {new Date(a.due_date).toLocaleDateString()}</div>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 shrink-0">
                        {a.status !== "done" && <Btn variant="secondary" className="!py-1 !px-2 text-xs" data-testid={`complete-${a.id}`} onClick={() => complete(a.id)}><Check size={13} /> Complete</Btn>}
                        {a.status === "done" && <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => setFollowFrom(a)}>Follow-up</Btn>}
                      </div>
                    )}
                  </div>
                ))}
              </Panel>
            </div>
          ))}
        </div>
      )}

      {createOpen && <TaskModal workspaceId={workspaceId} entities={entities} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {followFrom && <TaskModal workspaceId={workspaceId} entities={entities} seed={followFrom} onClose={() => setFollowFrom(null)} onSaved={() => { setFollowFrom(null); load(); }} />}
    </div>
  );
}

function buildSuggestions(acts, entities) {
  const out = [];
  const overdue = acts.filter((a) => a.status !== "done" && a.due_date && new Date(a.due_date) < new Date());
  if (overdue.length) out.push({ text: `Address ${overdue.length} overdue follow-up(s).`, reason: "Due follow-ups keep networks warm." });
  const unverified = entities.filter((e) => e.verification_status === "unverified" || e.relationship_stage === "candidate");
  if (unverified.length) out.push({ text: `Verify ${unverified.length} unverified candidate(s).`, reason: "Unverified candidates may hide useful connections." });
  const dnc = entities.filter((e) => !e.do_not_contact && !e.communication_preference);
  if (dnc.length) out.push({ text: "Record communication preferences where missing.", reason: "Respecting preferences before any outreach." });
  return out.slice(0, 3);
}

function TaskModal({ workspaceId, entities, onClose, onSaved, seed }) {
  const seedLinks = (seed?.entity_ids || []).map((id) => {
    const e = entities.find((x) => x.id === id);
    return { id, name: e?.name || id, type: e?.type };
  });
  const [f, setF] = useState({
    kind: seed ? "follow_up" : "task",
    title: seed ? `Follow up: ${seed.title}` : "",
    description: "", priority: "medium", due_date: "", reminder_date: "", outcome: "",
  });
  const [links, setLinks] = useState(seedLinks);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addLink = (rec) => { if (rec && !links.some((l) => l.id === rec.id)) setLinks((p) => [...p, rec]); };
  const removeLink = (id) => setLinks((p) => p.filter((l) => l.id !== id));
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try {
      await api.post(`/activities?workspace_id=${workspaceId}`, {
        ...f, entity_ids: links.map((l) => l.id),
        due_date: f.due_date ? new Date(f.due_date).toISOString() : null,
        reminder_date: f.reminder_date ? new Date(f.reminder_date).toISOString() : null,
        origin_ids: [],
      });
      toast.success("Action created"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={seed ? "Create follow-up" : "New action"} testid="task-modal">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Kind"><Select value={f.kind} onChange={(e) => set("kind", e.target.value)}>{KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}</Select></Field>
        <Field label="Priority"><Select value={f.priority} onChange={(e) => set("priority", e.target.value)}>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</Select></Field>
      </div>
      <Field label="Title"><Input data-testid="task-title" value={f.title} onChange={(e) => set("title", e.target.value)} autoFocus /></Field>
      <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Due date"><Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} /></Field>
        <Field label="Reminder (optional)"><Input type="date" value={f.reminder_date} onChange={(e) => set("reminder_date", e.target.value)} /></Field>
      </div>
      <div className="mb-3">
        {links.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="task-links">
            {links.map((l) => (
              <span key={l.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-text)" }}>
                {l.name}<button type="button" onClick={() => removeLink(l.id)} data-testid={`task-link-remove-${l.id}`} style={{ color: "var(--si-text-2)" }}>×</button>
              </span>
            ))}
          </div>
        )}
        <RecordPicker workspaceId={workspaceId} label="Link which contacts? (optional)" value={null}
          onChange={addLink} allowMe allowUnset typeFilter={null} testid="task-link" />
      </div>
      <Btn variant="primary" className="w-full" onClick={save} data-testid="task-save">{seed ? "Create follow-up" : "Create action"}</Btn>
    </Modal>
  );
}
